// Safely turn any axios error payload into a string suitable for React rendering.
// Handles:
//   - FastAPI/Pydantic 422: detail is an array of { type, loc, msg, input, ctx }
//   - Plain string detail
//   - Nested error objects
//   - Network / unknown errors
//
// Always returns a string — never an object or array — so callers can put
// the result directly inside JSX (`{formErr}`) without crashing React (#31).

function fieldFromLoc(loc) {
  if (!Array.isArray(loc)) return "";
  // Drop the first segment ("body" / "query" / etc.) — keep the field path.
  const tail = loc.slice(1).filter((p) => p !== null && p !== undefined);
  return tail.join(".");
}

export function formatApiError(err, fallback = "Something went wrong.") {
  // Network / no response
  if (err && !err.response) {
    return err.message || fallback;
  }

  const data = err?.response?.data;
  if (data == null) return fallback;

  // detail is the standard FastAPI shape
  const detail = data.detail ?? data;

  if (typeof detail === "string") return detail;

  if (Array.isArray(detail)) {
    const parts = detail.map((e) => {
      if (!e || typeof e !== "object") return String(e);
      const field = fieldFromLoc(e.loc);
      const msg = e.msg || e.message || "Invalid value";
      return field ? `${field}: ${msg}` : msg;
    });
    return parts.join(" • ");
  }

  if (typeof detail === "object") {
    if (typeof detail.msg === "string") return detail.msg;
    if (typeof detail.message === "string") return detail.message;
    try { return JSON.stringify(detail); } catch { return fallback; }
  }

  return String(detail) || fallback;
}

export default formatApiError;
